import React, { useEffect, useState } from "react";
import { Box, Typography, FormControl, InputLabel, Select, MenuItem, SelectChangeEvent, TextField, Divider, Button, CardMedia } from "@mui/material";
import { Form, FormikErrors, FormikTouched } from "formik";
import { useDropzone } from "react-dropzone";
import { AppColor, AppConstants } from "../../../../types/Constants";
import FileUpload from "../../../../components/FileUpload";
import FileUploadv2 from "../../../../components/FileUploadv2";
import FileUploadPreview from "../../../../components/FileUploadPreview";
import AppConfirmDialog from "../../../../components/AppConfirmDialog";
import { convertByteString, getImageURLOnServer } from "../../../../utils/utils";
import { BeaconDialogModes } from "./BeaconDialog";
import Resizer from 'react-image-file-resizer';
import { MakeBeacon } from "../../../../types/models/MakeBeacon";
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';

interface BeaconStyleFormProps {
  imageId1?: string,
  imageId2?: string,
  handleDeleteFace: (deleteFaceImage: boolean) => void,
  handleFileChange: (file: File | undefined, width: number, height: number) => void,
  handleFileChange2: (file: File | undefined) => void,
  mode?: typeof BeaconDialogModes[number]

  values: MakeBeacon,
  errors: FormikErrors<MakeBeacon>,
  handleChange: (e: React.ChangeEvent<any>) => void,
  setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => Promise<void | FormikErrors<MakeBeacon>>
  touched: FormikTouched<MakeBeacon>,
}

const BeaconStyleForm: React.FC<BeaconStyleFormProps> = ({ imageId1, imageId2, handleDeleteFace, handleFileChange, handleFileChange2, mode, values, errors, handleChange, setFieldValue, touched, }) => {

  const [uploadedFileForScreen1, setUploadedFileForScreen1] = useState<File | undefined>(undefined);
  const [uploadedFileForScreen2, setUploadedFileForScreen2] = useState<File | undefined>(undefined);
  const [openFileSizeExceededMessage, setOpenFileSizeExceededMessage] = useState<boolean>(false);
  const [openFileDimensionsExceededMessage, setOpenFileDimensionsExceededMessage] = useState<boolean>(false);
  const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });
  const [tempImageURL, setTempImageURL] = useState<string>("");
  // const [deleteFaceImage, setDeleteFaceImage] = useState(false);


  const dropzoneForScreen1 = useDropzone({
    accept: {
      'image/*': ['.jpeg', '.jpg'],
    },
    maxFiles: 1,
  });

  const dropzoneForScreen2 = useDropzone({
    accept: {
      'image/*': ['.jpeg', '.jpg'],
    },
    maxFiles: 1,
  });

  useEffect(() => {
    if (dropzoneForScreen1.acceptedFiles && dropzoneForScreen1.acceptedFiles.length >= 1) {
      const file = dropzoneForScreen1.acceptedFiles[0];
      // Check image file size
      if (file.size > AppConstants.MaxImageBytes) {
        resizeImage(file, AppConstants.MaxImageBytes, (resizedFile: File) => {
          const imageUrl = URL.createObjectURL(resizedFile);
          setUploadedFileForScreen1(resizedFile);
          getImageDimensions(resizedFile, (width, height) => {
            setImageDimensions({ width, height });
            handleFileChange(resizedFile, width, height);
          });
          setTempImageURL(imageUrl);
        });
      }
      else {
        const imageUrl = URL.createObjectURL(file);
        setUploadedFileForScreen1(file);
        getImageDimensions(file, (width, height) => {
          setImageDimensions({ width, height });
          handleFileChange(file, width, height);
        });
        setTempImageURL(imageUrl);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropzoneForScreen1.acceptedFiles]);

  // useEffect(() => {
  //   if (dropzoneForScreen2.acceptedFiles && dropzoneForScreen2.acceptedFiles.length >= 1) {
  //     // Check image dimensions to make sure its 256 x 256
  //     getImageDimensions(dropzoneForScreen2.acceptedFiles[0], (width, height) => {
  //       if (width == 256 && height == 256) {
  //         setUploadedFileForScreen2(dropzoneForScreen2.acceptedFiles[0]);
  //         handleFileChange2(dropzoneForScreen2.acceptedFiles[0]);
  //       }
  //       else {
  //         setOpenFileDimensionsExceededMessage(true);
  //       }
  //     });
  //   }

  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [dropzoneForScreen2.acceptedFiles]);

  const getImageDimensions = (file: File, callback: (width: number, height: number) => void) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        callback(img.width, img.height);
      };
      img.src = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  };

  const onDeleteUploadFileForScreen = (index: number) => {
    if (index == 1) {
      dropzoneForScreen1.acceptedFiles.splice(0, 1);
      setUploadedFileForScreen1(undefined);
      setImageDimensions({width: 0, height: 0});
      handleFileChange(undefined, 0, 0);
      setTempImageURL("");
    }
    else if (index == 2) {
      dropzoneForScreen2.acceptedFiles.splice(0,1);
      setUploadedFileForScreen2(undefined);
      handleFileChange2(undefined);
      // setDeleteFaceImage(true);
      // handleDeleteFace(true);
    }

  };

  const resizeImage = (file: File, maxSize: number, callback: (resizedFile: File) => void) => {
    Resizer.imageFileResizer(
      file,
      1000, // new image max width
      1000, // new image max height
      'JPEG', // default type
      100, // new image quality
      0, // rotation degree
      (blobFile) => {
        if (blobFile instanceof Blob && blobFile.size <= maxSize) {
          const resizedFile = new File([blobFile], file.name);
          callback(resizedFile);
        } else {
          setOpenFileSizeExceededMessage(true);
        }
      },
      'blob' // output type
    );
  };

  const handleModalChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputFile = event.target.files![0];
    
    if (inputFile.size > AppConstants.MaxImageBytes) {
      resizeImage(inputFile, AppConstants.MaxImageBytes, (resizedFile: File) => {
        const imageUrl = URL.createObjectURL(resizedFile);
        setUploadedFileForScreen1(resizedFile);
        getImageDimensions(resizedFile, (width, height) => {
          setImageDimensions({ width, height });
          handleFileChange(resizedFile, width, height);
        });
        setTempImageURL(imageUrl);
      });
    }
    else {
      const imageUrl = URL.createObjectURL(inputFile);
      setUploadedFileForScreen1(inputFile);
      getImageDimensions(inputFile, (width, height) => {
        setImageDimensions({ width, height });
        handleFileChange(inputFile, width, height);
      });
      setTempImageURL(imageUrl);
    }

    event.target.value = "";
  };
  
  const handleLink = (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value;

    // Only prepend https:// if the link doesnt have http:// or https://
    if (value && !/^https?:\/\//i.test(value)) {
      value = 'https://' + value;
    } 

    setFieldValue("link", value);
  };

  return (
    <>
      <Form style={{ width: "100%", marginTop: 16 }}>
        {/* <FormControl fullWidth sx={{ mb: 2 }}>
        <TextField
          disabled={mode === "MOD_ONLY" || mode === "READ_ONLY"}
          sx={{ padding: 2, marginTop: 3 }}
          required
          fullWidth
          variant="outlined"
          name="model"
          id="model"
          select
          label="3DModel"
          value={values.model ?? 'ball'}
          error={((errors.model ? true : undefined) && touched.model)}
          onChange={handleChange}
          helperText={errors.model && touched.model ? errors.model : null}>
            <MenuItem value={"ball"}>Ball</MenuItem>
            <MenuItem value={"planet"}>Planet</MenuItem>
            <MenuItem value={"trisign"}>Tri-Sign</MenuItem>
        </TextField>
        </FormControl> */}
        <Typography sx={{ fontSize: 16, fontWeight: 500 }}>Image</Typography>
        <Box sx={{ display: "flex", gap: 10, marginTop: 1, padding: 2 }}>
        {/* <Box sx={{ display: "flex", flexDirection: "column", gap: 2, marginTop: 1, padding: 2 }}>
          <FileUploadv2 
            dropzone={dropzoneForScreen1}
            readonly={mode === "READ_ONLY"}
            imageId={imageId1}
            imageURL={tempImageURL}
            modal={true} */}
              <Box
                sx={{
                  border: 'dashed 2px silver',
                  borderRadius: 2.5,
                  p: 3,
                  textAlign: 'center',
                  color: 'text.secondary',
                  backgroundColor: 'background.default',
                  alignContent: 'center',
                  width: 128,
                  height: 128
                }}
              >
                {imageId1 || tempImageURL ? (
                    <CardMedia
                    sx={{ width: "100%", height: "100%", objectFit: "cover", overflow: "hidden" }}
                    component="img"
                    src={tempImageURL ? tempImageURL : getImageURLOnServer(imageId1 ?? "")}
                    alt="beacon"
                  />
                ) : (
                    <ImageOutlinedIcon
                    style={{
                      fontSize: 40,
                      color: 'silver',
                    }}
                  />
                )}
            </Box>
          <Box sx={{ display: "flex", alignItems: "end", mb: 1 }}>
            <label htmlFor="file-input-modal">
            <Button
              component="span"
              variant="outlined"
              sx={{
                whiteSpace: "nowrap",
                height: 40,
              }}>
                Upload Custom Image
            </Button>
            </label>
            <input
              id="file-input-modal"
              type="file"
              accept=".png,.jpg,jpeg"
              style={{ display: 'none' }}
              onChange={handleModalChange}
            />
          </Box>
          {/* <FileUploadv2 
            dropzone={dropzoneForScreen2}
            readonly={mode === "READ_ONLY"}
            imageId={imageId2}
            modal={true}
          /> */}
        </Box>
        {(uploadedFileForScreen1 || imageId1 != undefined) &&
            <Box sx={{ paddingX: 2 }}>
              <FileUploadPreview
                file={uploadedFileForScreen1}
                imageId={imageId1}
                onDeleteUploadFile={() => onDeleteUploadFileForScreen(1)}
              />
            </Box>}
        <Divider sx={{ my: 4 }} />
        <Typography sx={{ fontSize: 16, fontWeight: 500 }}>Details Text</Typography>
        <TextField
          disabled={mode === "MOD_ONLY" || mode === "READ_ONLY"}
          sx={{ padding: 2, marginTop: 3 }}
          value={values.description}
          minRows={10}
          name="description"
          variant="outlined"
          multiline
          size="medium"
          fullWidth
          id="description"
          placeholder="..."
          InputLabelProps={{ shrink: true }}
          error={((errors.description ? true : undefined) && touched.description)}
          onChange={handleChange}
          helperText={errors.description && touched.description ? errors.description : `${values.description.length}/${1024}`}
          inputProps={{ maxLength: 1024 }}
          FormHelperTextProps={{
            sx: {
              textAlign: 'right',
            },
          }}
        />
        <Divider sx={{ my: 4 }} />
        <Typography sx={{ fontSize: 16, fontWeight: 500 }}>Link</Typography>
        <Typography sx={{ fontSize: 14, paddingLeft: 2, paddingTop: 2 }}>URL (Optional)</Typography>
        <TextField
          disabled={mode === "MOD_ONLY" || mode === "READ_ONLY"}
          sx={{ padding: 2 }}
          name="link"
          value={values.link}
          variant="outlined"
          fullWidth
          id="link"
          InputLabelProps={{ shrink: true }}
          onChange={handleChange}
          error={((errors.link ? true : undefined) && touched.link)}
          helperText={errors.link && touched.link ? errors.link : null}
        />
        {/* <Box>
          <Typography variant="h6" color="grey" ml={3} mb={1}>Beacon Modal Image</Typography>
          <FileUpload
            dropzone={dropzoneForScreen1}
            readonly={mode === "READ_ONLY"}
          />
          <Box sx={{ display: "flex", padding: 2, marginTop: 3, justifyContent: "flex-start", alignItems: "center" }}>
            <Box sx={{ marginLeft: 2 }}>
              <Typography sx={{ color: AppColor.subHeaderText }}>This will appear when the user taps on the beacon.</Typography>
            </Box>
          </Box>
          {(uploadedFileForScreen1 || imageId1) &&
            <Box sx={{ paddingX: 2 }}>
              <FileUploadPreview
                file={uploadedFileForScreen1}
                imageId={imageId1}
                onDeleteUploadFile={() => onDeleteUploadFileForScreen(1)}
              />
            </Box>}
        </Box>
        <Box sx={{marginTop: 6}}>
          <Typography variant="h6" color="grey" ml={3} mb={1}>Beacon Face Image</Typography>
          <FileUpload
            dropzone={dropzoneForScreen2}
            readonly={mode === "READ_ONLY"}
          />
          <Box sx={{ display: "flex", padding: 2, marginTop: 3, justifyContent: "flex-start", alignItems: "center" }}>
            <Box sx={{ marginLeft: 2 }}>
              <Typography sx={{ color: AppColor.subHeaderText }}>This will appear on the beacon face.</Typography>
            </Box>
          </Box>
          {(uploadedFileForScreen2 || (!deleteFaceImage && imageId2 != undefined)) &&
            <Box sx={{ paddingX: 2 }}>
              <FileUploadPreview
                file={uploadedFileForScreen2}
                imageId={imageId2}
                onDeleteUploadFile={() => onDeleteUploadFileForScreen(2)}
              />
            </Box>}
        </Box> */}
        <AppConfirmDialog
          open={openFileSizeExceededMessage}
          onConfirm={() => setOpenFileSizeExceededMessage(false)}
          confirmButtonTitle="OK"
          disableDenyButton={true}
          title={`The image is too large. Beacon images have a maximum filesize of ${convertByteString(AppConstants.MaxImageBytes)}. Please try resizing the image then uploading it again.`}
          dialogTitle="Problem attaching image to beacon"
          content=""
        />
        <AppConfirmDialog
          open={openFileDimensionsExceededMessage}
          onConfirm={() => setOpenFileDimensionsExceededMessage(false)}
          confirmButtonTitle="OK"
          disableDenyButton={true}
          title={`The image is not 256x256. Please try resizing the image then uploading it again.`}
          dialogTitle="Problem attaching image to beacon"
          content=""
        />
      </Form>
    </>
  );
};

export default BeaconStyleForm;
